import React from "react";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
} from "@material-tailwind/react";
import { CheckCircle, AlertCircle, XCircle, FileText } from "lucide-react";

interface ExcelUploadFeedbackProps {
  open: boolean;
  status: number;
  message: string;
  onClose: () => void;
  downloadUrl?: string;
}

const ExcelUploadFeedback: React.FC<ExcelUploadFeedbackProps> = ({
  open,
  status,
  message,
  onClose,
  downloadUrl,
}) => {
  const isSuccess = status === 200;
  const isPartial = status >= 400 && status < 500;
  const isError = status >= 500 || status === 0;

  const getStatusInfo = () => {
    if (isSuccess) {
      return {
        icon: (
          <CheckCircle className="w-12 h-12 md:w-16 md:h-16 text-green-500" />
        ),
        title: "Import réussi !",
        subtitle: "Le rapport de création est envoyé par mail",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        textColor: "text-green-800",
        buttonColor: "bg-green-600 hover:bg-green-700",
      };
    } else if (isPartial) {
      return {
        icon: (
          <AlertCircle className="w-12 h-12 md:w-16 md:h-16 text-yellow-500" />
        ),
        title: "Import partiel",
        subtitle: "Le rapport de création est envoyé par mail",
        bgColor: "bg-yellow-50",
        borderColor: "border-yellow-200",
        textColor: "text-yellow-800",
        buttonColor: "bg-yellow-600 hover:bg-yellow-700",
      };
    } else {
      return {
        icon: <XCircle className="w-12 h-12 md:w-16 md:h-16 text-red-500" />,
        title: "Erreur d'import",
        subtitle: "L'import n'a pas pu être effectué",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
        textColor: "text-red-800",
        buttonColor: "bg-red-600 hover:bg-red-700",
      };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Dialog
      open={open}
      handler={onClose}
      size="sm"
      className="m-4"
      style={{ zIndex: 9999 }}
    >
      <div
        className={`p-4 md:p-6 ${statusInfo.bgColor} border ${statusInfo.borderColor} rounded-lg`}
      >
        {/* Header avec animation */}
        <div className="text-center mb-4">
          <div
            className={`inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-white shadow-lg mb-3 ${
              isSuccess ? "animate-bounce" : ""
            }`}
          >
            {statusInfo.icon}
          </div>
          <h2
            className={`text-lg md:text-xl font-bold mb-1 ${statusInfo.textColor}`}
          >
            {statusInfo.title}
          </h2>
          <p className={`text-sm md:text-base mb-2 ${statusInfo.textColor}`}>
            {statusInfo.subtitle}
          </p>
          <p className="text-gray-600 text-sm md:text-base">{message}</p>
        </div>

        {/* Détails de l'import */}
        <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-[#131CC9]" />
            <h3 className="text-sm font-semibold text-gray-800">
              Détails de l'import
            </h3>
          </div>
          <div className="space-y-1 text-sm text-gray-600">
            <p>• Fichier Excel traité avec succès</p>
            {isSuccess && <p>• Le rapport de création est envoyé par mail</p>}
            {isPartial && <p>• Le rapport de création est envoyé par mail</p>}
            {isError && <p>• Erreur lors du traitement du fichier</p>}
            {downloadUrl && (
              <p>• Rapport détaillé téléchargé automatiquement</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end pt-3 border-t border-gray-200">
          <Button
            onClick={onClose}
            className={`px-4 md:px-6 py-2 md:py-3 rounded-lg transition-colors text-sm md:text-base text-white ${statusInfo.buttonColor}`}
          >
            {isSuccess ? "Continuer" : "Fermer"}
          </Button>
        </div>

        {/* Conseils */}
        {!isSuccess && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">
              Conseils pour résoudre le problème :
            </h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {isPartial && (
                <>
                  <li>
                    • Vérifiez que tous les champs obligatoires sont remplis
                  </li>
                  <li>
                    • Assurez-vous que les emails ne sont pas déjà utilisés
                  </li>
                  <li>
                    • Vérifiez le format des données (dates, téléphones, etc.)
                  </li>
                </>
              )}
              {isError && (
                <>
                  <li>
                    • Vérifiez que le fichier est au format Excel (.xlsx, .xls)
                  </li>
                  <li>• Assurez-vous que le fichier n'est pas corrompu</li>
                  <li>• Vérifiez votre connexion internet</li>
                  <li>• Réessayez dans quelques minutes</li>
                </>
              )}
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ExcelUploadFeedback;
